'use client';

import { useTranslations, useFormatter } from 'next-intl';
import { ChevronRight } from 'lucide-react';
import type { MyLeaderboardPosition } from '@impact/types';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { Flag } from '@/components/ui/flag/flag';
import { RankBadge } from './RankBadge';

export function MyPositionBanner({
  position,
  currency,
  onJumpToPage,
}: {
  position: MyLeaderboardPosition;
  currency: string;
  onJumpToPage: (page: number) => void;
}) {
  const t = useTranslations('leaderboard');
  const format = useFormatter();
  const { participant, rank, page } = position;

  return (
    <button
      type="button"
      onClick={() => onJumpToPage(page)}
      className="w-full border-t border-stone-100 bg-dgr-gold/5 border-l-4 border-l-dgr-gold px-4 py-3 md:px-6 md:py-4 hover:bg-dgr-gold/10 transition-colors text-left cursor-pointer"
    >
      {/* Mobile layout */}
      <div className="md:hidden">
        <p className="text-xs font-semibold text-dgr-gold-hover uppercase tracking-wide mb-2">
          {t('yourPosition')}
        </p>
        <div className="flex items-center gap-3">
          <RankBadge rank={rank} />
          <div className="w-8 h-8 rounded-full bg-stone-200 overflow-hidden flex-shrink-0">
            {participant.profileImageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={participant.profileImageUrl}
                alt={participant.name}
                className="object-cover w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-500 font-medium text-xs">
                {participant.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-stone-900 font-medium truncate text-sm">{participant.name}</p>
          </div>
          <div className="text-right flex-shrink-0 flex items-center gap-2">
            <p className="text-base font-bold text-dgr-gold-hover">
              {formatPublicCurrency(format, participant.raised, currency)}
            </p>
            <ChevronRight className="w-4 h-4 text-dgr-gold-hover" />
          </div>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:flex md:items-center md:gap-4">
        <p className="text-xs font-semibold text-dgr-gold-hover uppercase tracking-wide whitespace-nowrap">
          {t('yourPosition')}
        </p>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <RankBadge rank={rank} />
          <div className="w-10 h-10 rounded-full bg-stone-200 overflow-hidden flex-shrink-0">
            {participant.profileImageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={participant.profileImageUrl}
                alt={participant.name}
                className="object-cover w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-500 font-medium">
                {participant.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </div>
            )}
          </div>
          <p className="text-stone-900 font-medium truncate">{participant.name}</p>
          <Flag code={participant.countryCode} size="sm" />
          <p className="text-stone-500 text-sm truncate">{participant.cityName}</p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <p className="text-xl font-bold text-dgr-gold-hover">
            {formatPublicCurrency(format, participant.raised, currency)}
          </p>
          <span className="text-sm text-dgr-gold-hover flex items-center gap-1">
            {t('jumpToYourRank')}
            <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </button>
  );
}
